import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { Redirect } from "react-router-dom";
import Navbar from "./Navbar";
import ChapterDrawer from "./ChapterDrawer";
import Landing from "./Landing";
import { Answer, Enrolment } from "../../../stores/EnrolmentStore/Types";
import {
  QuestionPreview,
  TopicPreview,
} from "../../../stores/CoursePreviewStore/Types";
import { getRequest, postRequest } from "../../../utils/httpRequest";
import { errorNotification } from "../../../utils/helpers";
import Certificate from "../Certificate";
import { NotificationResource } from "../../../stores/RootStore/Types";
import RecentNotifications from "./RecentNotifications";
import { getLastLesson } from "../helpers";
import { CurrentUser } from "../../../stores/CurrentUserStore/Types";
import AiChat from "./AiChat";
import Icons from "./Icons";
import LessonFooter from "./LessonFooter";

interface Props {
  match: any;
  mode: string;
  getXp: (userId: number) => void;
  currentUser: CurrentUser;
  location: {
    search: string;
  };
}

interface State {
  enrolment: Enrolment;
  lesson: QuestionPreview;
  answer: Answer;
  loadingLesson: boolean;
  showSidebar: boolean;
  locked: boolean;
  chapters: TopicPreview[];
  answers: Answer[];
  notifications: NotificationResource[];
  loadingAnswer: boolean;
  showAiChat: boolean;
}

class Lesson extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      enrolment: null,
      lesson: null,
      answer: null,
      chapters: [],
      answers: [],
      notifications: [],
      loadingLesson: true,
      showSidebar: false,
      locked: false,
      loadingAnswer: true,
      showAiChat: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const enrolmentId = this.props.match.params.enrolmentId;

    return getRequest(`/api/enrolments/${enrolmentId}.json`)
      .then((response: AxiosResponse) => {
        this.setState({ enrolment: response.data }, () => {
          this.loadLesson();
          this.loadChapters(response.data);
          this.loadAnswers(response.data);
          this.getRecentNotifications();

          if (!response?.data?.enrolment?.sample) {
            $("chat-app").css({ display: "block" });
          }
        });
      })
      .catch((error) => {
        (window as any).honeybadger?.notify(error?.response?.data?.error);
      });
  }

  componentDidUpdate(nextProps: Readonly<Props>, nextContext: any) {
    if (
      this.props.match.params.questionId &&
      nextProps.match.params.questionId != this.props.match.params.questionId
    ) {
      this.setState({ loadingLesson: true, answer: null, loadingAnswer: true });
      window.scrollTo(0, 0);
      this.loadEnrolment(nextProps.match.params.enrolmentId);
      this.getRecentNotifications();
    }
  }

  getRecentNotifications = () => {
    getRequest("/api/notifications/recent.json")
      .then((response: AxiosResponse<NotificationResource[]>) => {
        this.setState({ notifications: response.data });
      })
      .catch((error) => {
        (window as any).honeybadger?.notify(error?.response?.data?.error);
      });
  };

  /**
   * Whether a response that was requested for `lessonId` still belongs on
   * screen, or whether the learner has moved on while it was in flight.
   *
   * The answer list is the slow one — it serialises every answer in the course,
   * and by the end of a course that is a six-figure payload. Submitting an
   * answer kicks one off, and the well-done modal then puts Continue a single
   * click away, so the stale response routinely landed after the next lesson
   * had already loaded and overwrote it with the previous lesson's answer.
   */
  stillOnLesson = (lessonId) => this.props.match.params.questionId === lessonId;

  loadLesson = () => {
    const { enrolment } = this.state;
    const lessonId = this.props.match.params.questionId;

    return getRequest(
      `/api/courses/${enrolment?.course?.slug}/questions/${lessonId}.json`,
    )
      .then((response: AxiosResponse) => {
        if (!this.stillOnLesson(lessonId)) return;

        this.setState({ lesson: response.data, loadingLesson: false });
      })
      .catch((error) => {
        this.setState({ loadingLesson: false });
        (window as any).honeybadger?.notify(error?.response?.data?.error);
        errorNotification(
          "Error loading lesson",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  getAnswer = () => {
    const { enrolment } = this.state;
    const lessonId = this.props.match.params.questionId;

    return getRequest(
      `/api/enrolments/${enrolment.id}/questions/${lessonId}/answer.json`,
    )
      .then((response: AxiosResponse) => {
        if (!this.stillOnLesson(lessonId)) return;

        this.setState({ answer: response.data, loadingAnswer: false });
      })
      .catch((error) => {
        this.setState({ loadingAnswer: false });
        (window as any).honeybadger?.notify(error?.response?.data?.error);
        errorNotification(
          "Error loading answer",
          JSON.stringify(error.response.data),
        );
      });
  };

  loadEnrolment = (enrolmentId) => {
    return getRequest(`/api/enrolments/${enrolmentId}.json`)
      .then((response: AxiosResponse) => {
        if (response.data.embedded_host_url) {
          window.location = response.data.embedded_host_url;
        }

        if (!response?.data?.enrolment?.sample) {
          $("chat-app").css({ display: "block" });
        }

        this.setState({ enrolment: response.data });
        this.loadLesson();
        this.loadTopics();
      })
      .catch((error) => {
        (window as any).honeybadger?.notify(error?.response?.data?.error);
      });
  };

  loadAnswers = (enrolment) => {
    const lessonId = this.props.match.params.questionId;
    const { lesson } = this.state;

    this.setState({ locked: false, loadingAnswer: true });
    // view=nav: this list exists to colour in the chapter list, so it asks for
    // question_id and state and nothing else. The full serializer embeds a
    // whole enrolment -- course, user, next question, recommendations -- inside
    // every answer, along with comments and rendered markdown, which is several
    // hundred KB on this page alone and grows with every lesson completed.
    //
    // The one answer this page actually renders needs all of that, so it is
    // fetched on its own rather than picked out of the list.
    return getRequest(`/api/answers.json?enrolment_id=${enrolment?.id}&view=nav`)
      .then((response: AxiosResponse) => {
        // Superseded — the request for the lesson now on screen will set this.
        if (!this.stillOnLesson(lessonId)) return;

        const answers = response.data;
        const answered = answers?.find(
          (answer) => answer.question_id == lessonId,
        );

        this.setState({ answers });

        if (
          enrolment.locked &&
          !["passed", "failed"].includes(answered?.state || "")
        ) {
          this.setState({ answer: null, locked: true, loadingAnswer: false });
        } else if (answered) {
          this.getAnswer();
        } else {
          this.setState({ answer: null, loadingAnswer: false });

          if (enrolment.locked) {
            if (!["passed", "failed"].includes(enrolment.state)) {
              this.setState({ locked: true });
            }
          } else if (enrolment?.preview && !lesson?.preview) {
            this.setState({ locked: true });
          }
        }
      })
      .catch((error) => {
        (window as any).honeybadger?.notify(error?.response?.data?.error);
        this.setState({ loadingAnswer: false });
        errorNotification(
          "Error loading lesson",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  loadChapters = (enrolment) => {
    return getRequest(`/api/courses/${enrolment?.course.slug}/topics.json?view=nav`)
      .then((response: AxiosResponse) => {
        this.setState({ chapters: response.data });
      })
      .catch((error) => {
        (window as any).honeybadger?.notify(error?.response?.data?.error);
        errorNotification(
          "Error loading lesson",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  loadTopics = () => {
    const { enrolment } = this.state;

    return getRequest(`/api/courses/${enrolment?.course.slug}/topics.json?view=nav`)
      .then((response: AxiosResponse) => {
        this.setState({ chapters: response.data });
        this.loadAnswers(enrolment);
      })
      .catch((error) => {
        (window as any).honeybadger?.notify(error?.response?.data?.error);
        errorNotification(
          "Error loading lesson",
          JSON.stringify(error?.response?.data),
        );
      });
  };

  handleShowSidebar = () => {
    this.setState({ showSidebar: true });
  };

  handleHideSidebar = () => {
    this.setState({ showSidebar: false, showAiChat: false });
  };

  refreshAnswers = () => {
    const { enrolment } = this.state;
    this.loadAnswers(enrolment);
  };

  getXp = () => {
    this.props.getXp(this.props.currentUser.id);
  };

  handleShowAiChat = (e, value) => {
    e.preventDefault();

    this.setState({ showAiChat: value });
  };

  render() {
    const {
      enrolment,
      lesson,
      answer,
      chapters,
      answers,
      showSidebar,
      loadingLesson,
      locked,
      notifications,
      loadingAnswer,
      showAiChat,
    } = this.state;

    const {
      mode,
      currentUser,
      getXp,
      match: { params },
      location: { search },
    } = this.props;

    const lessonId = params.questionId;
    const lastLesson = getLastLesson(chapters);

    return (
      <>
        <Icons />
        <div
          className={`ui facelift-course-inside facelift-new-homepage  course-lesson ${mode}-lesson`}
        >
          {locked && (
            <Redirect to={{ pathname: `/enrolments/${params.enrolmentId}` }} />
          )}
          {chapters.length > 0 && (
            <>
              <ChapterDrawer
                showSidebar={showSidebar}
                handleHideSidebar={this.handleHideSidebar}
                enrolment={enrolment}
                lesson={lesson}
                chapters={chapters}
                answers={answers}
                currentUser={currentUser}
              />
              <Navbar
                handleShowSidebar={this.handleShowSidebar}
                enrolment={enrolment}
                lesson={lesson}
                handleShowAiChat={this.handleShowAiChat}
              />
            </>
          )}

          {mode === "certificate" ? (
            <Certificate
              enrolment={enrolment}
              enrolmentId={params.enrolmentId}
              currentUser={currentUser}
            />
          ) : (
            <Landing
              lesson={lesson}
              lessonId={lessonId}
              enrolment={enrolment}
              answer={answer}
              lastLesson={lastLesson}
              refreshAnswers={this.refreshAnswers}
              loadingLesson={loadingLesson}
              loadingAnswer={loadingAnswer}
              getRecentNotifications={this.getRecentNotifications}
              getXp={this.getXp}
              page={mode}
              currentUser={currentUser}
              enrolmentId={params.enrolmentId}
              answerId={params.answerId}
              getAnswer={this.getAnswer}
              search={search}
            />
          )}
          {mode !== "certificate" && <LessonFooter />}
          <AiChat
            showAiChat={showAiChat}
            handleShowAiChat={this.handleShowAiChat}
            enrolmentId={params.enrolmentId}
            lessonId={lessonId}
          />
          <RecentNotifications notifications={notifications} />
        </div>
      </>
    );
  }
}

export default Lesson;
